/**
 * Payment Method Selector
 * 
 * Handles payment method selection and toggles between card form and iframe
 * 
 * @package betbeast
 */

export class PaymentMethodSelector {
    constructor(modalElement, depositMethodsAPI = null, validator = null, currencyDisplayManager = null, schemaRenderer = null, onMethodChange = null) {
        this.modal = modalElement;
        this.depositMethodsAPI = depositMethodsAPI;
        this.validator = validator;
        this.currencyDisplayManager = currencyDisplayManager;
        this.schemaRenderer = schemaRenderer;
        this.onMethodChange = onMethodChange || (() => {});
        this.selectedMethod = null;
        this.selectedAPICode = null;
        this.selectedVariantKey = null;
        // PSP limits for the currently-selected method. Kept as the stable base so the
        // controller can intersect them with the selected bonus's range (and widen back
        // when the bonus is deselected) instead of reading the already-tightened input.
        this.currentLimits = { min: null, max: null };
        
        if (!this.modal) {
            console.warn('Modal element not provided to PaymentMethodSelector');
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize payment method selection listeners
     */
    init() {
        const paymentMethodButtons = this.modal.querySelectorAll('.wallet-deposit-payment-method-item');
        
        paymentMethodButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const variantKey = button.getAttribute('data-variant-key');
                this.selectPaymentMethod(variantKey, button);
            });
        });
        
        // Auto-select first visible method
        this.selectFirstAvailableMethod();
    }
    
    /**
     * Select a payment method
     * @param {string} method - Payment method identifier (visa, revolut, bank, etc.)
     */
    selectPaymentMethod(variantKey, buttonElement = null) {
        if (!variantKey) return;

        // Resolve the selected card. Several cards can share an API code (variants),
        // so the variant key — not the code — identifies a single card.
        const btn = buttonElement ?? this.modal.querySelector(
            `.wallet-deposit-payment-method-item[data-variant-key="${variantKey}"]:not(.wallet-deposit-payment-method-item--mobile)`
        );

        // The API code is shared across variants and drives limits + submission.
        const method = btn ? btn.getAttribute('data-payment-method') : variantKey;

        this.selectedVariantKey = variantKey;
        this.selectedMethod = method;

        // Render extra schema fields for the selected card (if any). Fields the method
        // auto-fills from the player's currency are stripped server-side, so only the
        // player-fillable fields remain to render here.
        if (this.schemaRenderer) {
            this.schemaRenderer.renderForMethod(btn);
        }

        // Get API code if we have the API service
        if (this.depositMethodsAPI) {
            // The API code drives submission and bonus-plan lookups…
            this.selectedAPICode = this.depositMethodsAPI.mapUIMethodToAPICode(method);

            // …but limits are per card: variants sharing a code have their own ranges.
            // Looked up unconditionally so a card whose code doesn't resolve can't
            // silently inherit the previously selected card's limits.
            const limits = this.depositMethodsAPI.getLimitsForVariant(variantKey, this.selectedAPICode);
            this.currentLimits = { min: limits.min, max: limits.max };
            this.updateAmountLimits(limits);
        }

        // Update button active states
        this.updateButtonStates(variantKey);

        // Swap the fine print under the security badges for this card's notice.
        this.updateLegalNotice(btn);

        // Toggle between card form and iframe
        this.togglePaymentDisplay(method);

        // Notify listeners (e.g. bonus selector) that the method changed, so they
        // can re-fetch amount/method-specific data such as eligible bonus plans.
        this.onMethodChange(this.selectedAPICode || method);
    }
    
    /**
     * Refresh the per-method-card limit labels for the selected method.
     *
     * The amount input's own min/max are no longer set here: they are the
     * intersection of these PSP limits with the selected bonus's range, owned by
     * DepositFlowController.applyEffectiveLimits() (triggered via onMethodChange).
     *
     * @param {Object} limits - Object with min, max, feeRate
     */
    updateAmountLimits(limits) {
        // Use CurrencyDisplayManager for proper formatting of the per-method cards.
        if (this.currencyDisplayManager) {
            this.currencyDisplayManager.updatePaymentMethodLimits();
        }
    }

    /**
     * PSP limits for the currently-selected method (stable base for the effective
     * range computed by DepositFlowController). May contain nulls before a method
     * with known limits is selected.
     *
     * @returns {{min: number|null, max: number|null}}
     */
    getCurrentLimits() {
        return this.currentLimits;
    }
    
    /**
     * Select first available payment method
     */
    selectFirstAvailableMethod() {
        const visibleButtons = Array.from(this.modal.querySelectorAll('.wallet-deposit-payment-method-item'))
            .filter(btn => btn.style.display !== 'none' && !btn.hasAttribute('disabled'));
        
        if (visibleButtons.length > 0) {
            const firstButton = visibleButtons[0];
            this.selectPaymentMethod(firstButton.getAttribute('data-variant-key'), firstButton);
        }
    }
    
    /**
     * Update active state for payment method buttons
     * @param {string} activeMethod - Currently selected method
     */
    updateButtonStates(activeVariantKey) {
        const buttons = this.modal.querySelectorAll('.wallet-deposit-payment-method-item');

        buttons.forEach(button => {
            const variantKey = button.getAttribute('data-variant-key');

            if (variantKey === activeVariantKey) {
                button.classList.add('is-active');
            } else {
                button.classList.remove('is-active');
            }
        });
    }
    
    /**
     * Show the selected card's operator-configured legal notice (PSP trademark
     * attributions, e.g. Interac's) under the security badges, or hide the line
     * entirely for cards that have none.
     *
     * @param {HTMLElement|null} button - The selected method button.
     */
    updateLegalNotice(button) {
        const notice = this.modal.querySelector('.wallet-deposit-security-legal');
        if (!notice) return;

        const text = button ? (button.getAttribute('data-legal-text') || '') : '';

        // textContent, not innerHTML — the value is operator-entered plain text.
        notice.textContent   = text;
        notice.style.display = text ? '' : 'none';
    }

    /**
     * Toggle between card form and iframe based on payment method
     * @param {string} method - Payment method identifier
     */
    togglePaymentDisplay(method) {
        const iframeContainer = this.modal.querySelector('[data-payment-type="iframe"]');
        const iframeTitle = this.modal.querySelector('.wallet-deposit-iframe-title');
        const amountSection = this.modal.querySelector('.wallet-deposit-amount-section');
        const submitSection = this.modal.querySelector('.wallet-deposit-submit-section');
        
        if (!iframeContainer) return;
        
        // Always show iframe, amount section, and submit section
        iframeContainer.style.display = 'block';
        if (amountSection) amountSection.style.display = 'block';
        if (submitSection) submitSection.style.display = 'flex';
        
        // Update iframe title based on method
        if (iframeTitle) {
            iframeTitle.textContent = this.getPaymentMethodTitle(method);
        }
    }
    
    /**
     * Get display title for payment method
     * @param {string} method - Payment method identifier
     * @returns {string} Display title
     */
    getPaymentMethodTitle(method) {
        // Try to get name from API data first
        if (this.depositMethodsAPI) {
            const methodName = this.depositMethodsAPI.getMethodName(method);
            if (methodName && methodName !== method) {
                return methodName;
            }
        }
        
        // Fallback titles for API codes
        const titles = {
            'APCOPAY_V2_CARD': 'Credit & Debit Card',
            'APCOPAY_V2_COINPAYMENTS': 'Crypto',
            'APCOPAY_V2_INTERAC': 'Interac',
            'APCOPAY_V2_SEKOPENBANKING': 'Open Banking',
            'APCOPAY_V2_CASHLIB': 'CashLib'
        };
        
        return titles[method] || 'Payment Method';
    }
    
    /**
     * Get currently selected payment method
     * @returns {string} Selected method identifier
     */
    getSelectedMethod() {
        return this.selectedMethod;
    }
    
    /**
     * Load iframe for non-card payment methods
     * @param {string} iframeUrl - URL for the payment provider iframe
     */
    loadPaymentIframe(iframeUrl) {
        const iframeContainer = this.modal.querySelector('.wallet-deposit-iframe-container');
        const placeholder = this.modal.querySelector('.wallet-deposit-iframe-placeholder');
        
        if (!iframeContainer) return;
        
        // Remove placeholder
        if (placeholder) {
            placeholder.remove();
        }
        
        // Create and inject iframe
        const iframe = document.createElement('iframe');
        iframe.src = iframeUrl;
        iframe.setAttribute('allow', 'geolocation *;camera *;payment *;clipboard-read *;clipboard-write *;autoplay *;microphone *;fullscreen *;accelerometer *;magnetometer *;gyroscope *;picture-in-picture *;otp-credentials *;');
        iframe.setAttribute('data-hj-allow-iframe', 'true');
        iframe.style.border = 'none';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        
        iframeContainer.appendChild(iframe);
    }
    
    /**
     * Clear iframe content
     */
    clearPaymentIframe() {
        const iframeContainer = this.modal.querySelector('.wallet-deposit-iframe-container');
        if (!iframeContainer) return;
        
        // Remove existing iframe
        const existingIframe = iframeContainer.querySelector('iframe');
        if (existingIframe) {
            existingIframe.remove();
        }
        
        // Restore placeholder if not exists
        if (!iframeContainer.querySelector('.wallet-deposit-iframe-placeholder')) {
            const placeholder = document.createElement('div');
            placeholder.className = 'wallet-deposit-iframe-placeholder';
            placeholder.innerHTML = `
                <i class="fa fa-credit-card" aria-hidden="true"></i>
                <p>Payment provider will load here</p>
            `;
            iframeContainer.appendChild(placeholder);
        }
    }
}
